import { FileIcon, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import constant from 'lodash/constant'
import {
  OrderEventFilePreviewBreadcrumb,
  OrderEventFilePreviewEventDetails
} from '@klear/ui/components/order-event-file-preview-sidebar'
import { withAttachmentDisposition } from '@klear/upload/file-url'
import { useFileDownload } from '@klear/ui/lib/download-file'
import { resolveOrderEventPreviewFileUrl } from '@klear/ui/order-event-file-preview'
import type {
  OrderEventFilePreviewBreadcrumbItem,
  OrderEventFilePreviewContext
} from '@klear/order-event/file-preview-display'

interface PDFPreviewProps {
  context: OrderEventFilePreviewContext
  fileUrl: string
  fileName: string
  getEventUrl?: (item: OrderEventFilePreviewBreadcrumbItem) => string
}

export default function PDFPreview({ context, fileUrl, fileName, getEventUrl = constant('') }: PDFPreviewProps) {
  const [fullUrl, setFullUrl] = useState<string | null>(null)
  const { startDownload, isDownloading } = useFileDownload()

  useEffect(() => {
    if (!fileUrl) {
      console.error('No fileUrl provided to PDFPreview')
      return
    }
    try {
      setFullUrl(resolveOrderEventPreviewFileUrl(fileUrl))
    } catch (err) {
      console.error('Error constructing PDF URL:', err)
    }
  }, [fileUrl])

  if (!fileUrl) {
    return <div className="text-center text-red-500 py-10">No PDF URL provided</div>
  }

  return !fullUrl ? null : (
    <div className="flex flex-col">
      <h1 className="flex items-center text-2xl font-bold mb-4">
        <FileIcon className="w-4 h-4 mr-2" />
        <span>{fileName}</span>
        {fullUrl && (
          <button
            type="button"
            onClick={() => startDownload(withAttachmentDisposition(fullUrl), fileName)}
            disabled={isDownloading}
            className="ml-4 inline-flex items-center gap-2 px-3 py-1 bg-muted rounded hover:bg-muted/80 disabled:opacity-60"
          >
            <span>Download</span>
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                />
              </svg>
            )}
          </button>
        )}
      </h1>
      <OrderEventFilePreviewBreadcrumb context={context} getEventUrl={getEventUrl} />
      <div className="flex gap-4">
        <div className="w-1/4" style={{ maxHeight: '75vh' }}>
          <OrderEventFilePreviewEventDetails context={context} />
        </div>
        <div className="w-3/4">
          <div className="border rounded-lg p-4 bg-card shadow-sm w-full h-[70vh] overflow-auto">
            <iframe src={fullUrl} width="100%" height="100%" style={{ border: 'none' }} title="PDF Preview" />
          </div>
        </div>
      </div>
    </div>
  )
}
